import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role, UserDTO } from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Button, Card, ErrorState, Field, Input, Spinner } from '../components/ui';

const selectClass =
  'w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-indigo-500';

export function Users() {
  const qc = useQueryClient();
  const { status } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: UserDTO[] }>('/api/users'),
  });

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [formErr, setFormErr] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });

  const createMut = useMutation({
    mutationFn: () => api.post('/api/users', { username: username.trim(), password, role }),
    onSuccess: () => {
      setUsername('');
      setPassword('');
      setRole('user');
      setFormErr('');
      void invalidate();
    },
    onError: (e) => setFormErr(e instanceof ApiError ? e.message : 'Could not create user'),
  });

  const updateMut = useMutation({
    mutationFn: (v: { id: string; patch: { role?: Role; password?: string } }) =>
      api.patch(`/api/users/${v.id}`, v.patch),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.del(`/api/users/${id}`),
    onSuccess: invalidate,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || password.length < 8) {
      setFormErr('Enter a username and a password of at least 8 characters.');
      return;
    }
    createMut.mutate();
  };

  const resetPassword = (u: UserDTO) => {
    const pw = window.prompt(`New password for "${u.username}" (min 8 characters):`);
    if (pw == null) return;
    if (pw.length < 8) {
      window.alert('Password must be at least 8 characters.');
      return;
    }
    updateMut.mutate({ id: u.id, patch: { password: pw } });
  };

  const remove = (u: UserDTO) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    deleteMut.mutate(u.id);
  };

  const users = data?.users ?? [];
  const adminCount = users.filter((u) => u.role === 'admin').length;
  const mutError = (updateMut.error ?? deleteMut.error) as ApiError | null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-slate-400">
          Manage who can sign in. Admins can change Settings and manage users; regular users get the
          app (library, search, downloads) only.
        </p>
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-200">Add a user</h2>
        <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
          <div className="w-44">
            <Field label="Username">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
            </Field>
          </div>
          <div className="w-44">
            <Field label="Password" hint="Min 8 characters">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
          </div>
          <div className="w-32">
            <Field label="Role">
              <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={selectClass}>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </Field>
          </div>
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? 'Adding…' : 'Add user'}
          </Button>
        </form>
        {formErr && <p className="text-sm text-rose-400">{formErr}</p>}
      </Card>

      {isLoading && <Spinner />}
      {isError && <ErrorState message="Could not load users." onRetry={() => void refetch()} />}
      {data && (
        <Card className="space-y-0 p-0">
          <div className="grid grid-cols-[1fr_5rem_auto] gap-3 border-b border-slate-800 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            <span>User</span>
            <span>Role</span>
            <span className="text-right">Actions</span>
          </div>
          {users.map((u) => {
            const isSelf = u.username === status?.username;
            const isLastAdmin = u.role === 'admin' && adminCount <= 1;
            return (
              <div
                key={u.id}
                className="grid grid-cols-[1fr_5rem_auto] items-center gap-3 border-b border-slate-800/60 px-4 py-2.5 text-sm last:border-0"
              >
                <span className="font-medium text-slate-200">
                  {u.username}
                  {isSelf && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                </span>
                <Badge
                  className={u.role === 'admin' ? 'bg-indigo-600/20 text-indigo-200' : 'bg-slate-700/40 text-slate-300'}
                >
                  {u.role}
                </Badge>
                <div className="flex justify-end gap-2">
                  <Button variant="subtle" onClick={() => resetPassword(u)} disabled={updateMut.isPending}>
                    Reset password
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      updateMut.mutate({ id: u.id, patch: { role: u.role === 'admin' ? 'user' : 'admin' } })
                    }
                    disabled={updateMut.isPending || isLastAdmin}
                    title={isLastAdmin ? 'Cannot demote the last admin' : undefined}
                  >
                    {u.role === 'admin' ? 'Make user' : 'Make admin'}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => remove(u)}
                    disabled={deleteMut.isPending || isSelf || isLastAdmin}
                    title={
                      isSelf
                        ? 'You cannot delete yourself'
                        : isLastAdmin
                          ? 'Cannot delete the last admin'
                          : undefined
                    }
                  >
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}
      {mutError && <p className="text-sm text-rose-400">{mutError.message}</p>}
    </div>
  );
}
